import { Fragment, useState } from "react";
import GeniusDevelopmentLogo from "assets/img/genius-development.png";
import moment from "moment";
import { absTimeDifference } from "utils/timeDifference";
import RadarChart from "./RadarChart";
import styles from "./styles";
import { usePDF } from "hooks";
import { capitalize } from "lodash";

const primaryFontColor = "#262973";

const useStudentReportPDF = ({ studentReport, student }) => {
	const [chart, setChart] = useState(null);

	const StudentReportComponent = (
		<div className="text-dark mb-4" style={{ fontFamily: "sans" }}>
			<div style={{ height: 29.7 * 37.8 }}>
				<div className="text-center pt-4 pb-3" style={{ backgroundColor: "#E6F8FF" }}>
					<img src={GeniusDevelopmentLogo} alt="Genius Development Logo" style={{ width: 170 }} />
				</div>
				<div className="text-center font-weight-bold my-3" style={{ color: primaryFontColor, fontSize: "19px" }}>
					Genius Student Progress Report
				</div>
				<div className="my-3 mx-5">
					<div className="ml-1" style={{ color: primaryFontColor, fontSize: "16px", fontWeight: 700 }}>
						Section 1. Lesson information
					</div>
					<div style={{ borderTop: "solid #262973 1px", borderBottom: "solid #262973 1px" }}>
						<div className="my-2 mx-1" style={{ fontSize: "15px", color: "black" }}>
							<div className="row my-1">
								<div className="col-3">Student Name:</div>
								<div className="col-9 font-weight-bold">
									{student?.firstName} {student?.lastName}
								</div>
							</div>
							<div className="row my-1">
								<div className="col-3">Teacher Name:</div>
								<div className="col-9 font-weight-bold">{studentReport?.teacher?.name}</div>
							</div>
							<div className="row my-1">
								<div className="col-3">Date:</div>
								<div className="col-9 font-weight-bold">{moment(studentReport?.lesson_detail?.date).format("DD MMM YYYY")}</div>
							</div>
							<div className="row my-1">
								<div className="col-3">Class duration:</div>
								<div className="col-9 font-weight-bold">
									{absTimeDifference(studentReport?.lesson_detail?.start_time, studentReport?.lesson_detail?.end_time) / 60} hours
								</div>
							</div>
							<div className="row my-1">
								<div className="col-3">Subject:</div>
								<div className="col-9 font-weight-bold">{studentReport?.lesson_detail?.course.name}</div>
							</div>
							<div className="row my-1">
								<div className="col-3">Topic:</div>
								<div className="col-9 font-weight-bold">{studentReport?.lesson_detail?.lesson?.name}</div>
							</div>
						</div>
					</div>
				</div>
				<div className="mt-5 mx-5">
					<div className="ml-1" style={{ color: primaryFontColor, fontSize: "16px", fontWeight: 700 }}>
						Section 2. Analysis for domains of learning
					</div>
					<div className="ml-1 mt-2" style={{ fontSize: "14px", color: "black" }}>
						In this section, the number rating stands for the average performance on the aspects, following: 5 = outstanding, 4 = very
						good, 3 = good, 2 = fair, 1 = requires improvement. If less than 2, please illustrate more details.
					</div>

					<table style={{ width: "100%" }} className="mt-1">
						<thead>
							<tr style={styles.tableHead}>
								<th></th>
								<th style={styles.tableHeading}>Key aspects of learning</th>
								<th style={styles.tableHeading}>Mark</th>
								<th style={styles.tableHeading}>If less than 2, why?</th>
							</tr>
						</thead>
						<tbody>
							{studentReport?.traits?.map((trait, index) => (
								<tr style={styles.tableRow} key={index}>
									<td style={styles.tableNumbering}>{index + 1}</td>
									<td style={styles.traitName}>{trait.name}</td>
									<td style={styles.traitPerformance}>{trait.performance}</td>
									<td style={styles.traitComments}>{trait.comments}</td>
								</tr>
							))}
							<tr style={styles.tableLastRow}>
								<td colSpan={2} style={styles.totalScore}>
									Total score (out of 55)
								</td>
								<td colSpan={2} style={styles.totalPerformance}>
									{studentReport?.traits?.reduce(
										(accumulatedPerformance, trait) => accumulatedPerformance + Number(trait.performance),
										0
									)}
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>

			<div style={{ minHeight: 29.7 * 37.8 }}>
				<div className="mt-5 mx-5">
					<div className="font-weight-bold ml-1" style={{ color: primaryFontColor, fontSize: "16px" }}>
						The radar chart of all the domains:
					</div>
					<div className="border d-flex justify-content-center">
						{chart ? (
							<img src={chart} alt="Student performance chart" />
						) : (
							<RadarChart traits={studentReport?.traits} setChart={setChart} />
						)}
					</div>
				</div>
				<div className="my-5 mx-5">
					<div className="ml-1" style={{ color: primaryFontColor, fontSize: "16px", fontWeight: 700 }}>
						Section 3. Teacher Feedback
					</div>
					<div className="ml-1 mt-2" style={{ fontSize: "14px", color: "black" }}>
						In this section, the number rating stands for the average performance on the aspects, following: 5 = outstanding, 4 = very
						good, 3 = good, 2 = fair, 1 = requires improvement.
					</div>

					<table style={{ width: "100%" }} className="mt-1">
						<thead>
							<tr style={styles.tableHead}>
								<th style={{ ...styles.tableHeading, ...styles.learningObjectiveNameHeader }}>Learning Objectives</th>
								<th style={{ ...styles.tableHeading, ...styles.learningObjectivePerformanceHeader }}>Level of comprehension</th>
							</tr>
						</thead>
						<tbody>
							{studentReport?.learning_objectives?.map((learningObjective, index) => (
								<tr style={styles.tableRow} key={index}>
									<td style={styles.learningObjectiveName}>{capitalize(learningObjective.name)}</td>
									<td style={styles.learningObjectivePerformance}>{capitalize(learningObjective.performance)}</td>
								</tr>
							))}
							<tr style={styles.tableLastRow}>
								<td colSpan={2}>
									<div style={{ color: primaryFontColor, fontSize: 17, fontWeight: 700, paddingLeft: 5, paddingTop: 5 }}>
										Additional Comments:
									</div>
									<ul style={{ paddingLeft: 28 }}>
										{studentReport?.additional_comments?.map((additionalComment, index) => (
											<li key={index} style={{ marginBottom: 15, color: "black" }}>
												<div style={{ fontSize: 16 }}>{additionalComment.question}</div>
												<div style={{ fontSize: 16, fontWeight: 600 }}>{additionalComment.answer}</div>
											</li>
										))}
									</ul>
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>

			<div className="mb-5 mx-5">
				<div className="ml-1" style={{ color: primaryFontColor, fontSize: "16px", fontWeight: 700 }}>
					Section 4. Inquisitive Questions by Student
				</div>
				<div className="ml-1 mt-2" style={{ fontSize: "14px", color: "black" }}>
					What are the special questions asked by the student during class?
				</div>

				<table style={{ width: "100%" }} className="mt-1">
					<thead>
						<tr style={styles.tableHead}>
							<th style={{ ...styles.tableHeading, ...styles.classQuestionHeader }}>Questions By Student</th>
						</tr>
					</thead>
					<tbody>
						<tr style={styles.tableLastRow}>
							<td colSpan={2}>
								<ul style={{ paddingLeft: 28 }}>
									{studentReport?.class_questions?.map((classQuestion, index) => (
										<li key={index} style={{ marginBottom: 15, color: "black" }}>
											<div style={{ fontSize: 16 }}>{classQuestion.question}</div>
											<div style={{ fontSize: 16, fontWeight: 600 }}>{classQuestion.answer}</div>
										</li>
									))}
								</ul>
							</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	);

	return usePDF(
		StudentReportComponent,
		`${studentReport?.lesson_detail?.date}-${studentReport?.lesson_detail?.course.name}_${student?.firstName}_${student?.lastName}_${studentReport?.lesson_detail?.lesson?.name}`
	);
};

export default useStudentReportPDF;
